/**
 * Andhra Yatri — Official v2.0 API Service Client
 * Based on API_DOCUMENTATION.md & Backend OpenAPI Specs
 */

import { apiRequest, API_ROOT } from './andhraYatriApi';

// ── Types & Interfaces ──────────────────────────────────────────────────

export interface HomeBanner {
  id: string;
  title: string;
  image_url: string;
  action_url: string;
  subtitle?: string;
  badge?: string;
}

export interface HomeService {
  code: string;
  name: string;
  is_active: boolean;
  icon?: string;
}

export interface HomeDestination {
  id?: string;
  name: string;
  slug: string;
  category: string;
  image?: string;
  city?: string;
  state?: string;
  starting_price?: string | number;
  duration?: string;
  rating?: string | number;
}

export interface HomePilgrimage {
  id?: string;
  title: string;
  slug: string;
  temple_name?: string;
  city?: string;
  duration?: string;
  duration_days?: number;
  starting_price?: string | number;
  image_url?: string;
  highlights?: string[];
  rating?: string | number;
  is_featured?: boolean;
}

export interface HomeAnnouncement {
  id: string;
  title: string;
  message: string;
  announcement_type: 'INFO' | 'ALERT' | 'PROMO' | string;
  action_text?: string;
  action_url?: string;
  priority?: number;
  is_active?: boolean;
}

export interface UniqueStayCategory {
  id: string;
  name: string;
  type: string;
  icon: string;
  image: string;
  backendId: string;
  is_active: boolean;
  sort_order?: number;
}

export interface OutdoorActivityCategory {
  id: string;
  name: string;
  type: string;
  icon: string;
  image: string;
  backendId: string;
  is_active: boolean;
  sort_order?: number;
}

export interface HomeScreenData {
  banners: HomeBanner[];
  services: HomeService[];
  destinations: HomeDestination[];
  pilgrimages: HomePilgrimage[];
  announcements: HomeAnnouncement[];
  unique_stays: UniqueStayCategory[];
  activities: OutdoorActivityCategory[];
  recent_rides?: any[];
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// ── 6. Home Screen Aggregated API ───────────────────────────────────────

export const homeApi = {
  /**
   * Fetches the complete aggregated home screen data in a single round-trip.
   * Endpoint: GET /api/v1/home/
   */
  async getHome(): Promise<HomeScreenData> {
    const res = await apiRequest(`${API_ROOT}/home/`, { method: 'GET' });
    // Normalize if wrapped in { success, data }
    return (res?.data || res) as HomeScreenData;
  },
};

// ── 7-8. Rides Passenger & Outstation API ───────────────────────────────

export interface RideEstimateParams {
  pickup_latitude: number;
  pickup_longitude: number;
  drop_latitude: number;
  drop_longitude: number;
  pickup_address?: string;
  drop_address?: string;
}

export interface VehicleEstimateCategory {
  category: 'AUTO' | 'HATCHBACK' | 'SEDAN' | 'SUV' | string;
  display_name: string;
  fare: number;
  eta_minutes: number;
  capacity: number;
  distance_km?: number;
}

export interface RideEstimateResponse {
  distance_km: number;
  duration_minutes: number;
  categories: VehicleEstimateCategory[];
}

export interface RequestRidePayload {
  pickup_latitude: number;
  pickup_longitude: number;
  drop_latitude: number;
  drop_longitude: number;
  pickup_address: string;
  drop_address: string;
  vehicle_category: string;
  payment_method?: string;
}

export const ridesApi = {
  /**
   * Calculate fare estimates for all vehicle classes.
   * Endpoint: POST /api/v1/rides/estimate
   */
  async getEstimate(payload: RideEstimateParams): Promise<RideEstimateResponse> {
    const res = await apiRequest(`${API_ROOT}/rides/estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return (res?.data || res) as RideEstimateResponse;
  },

  /**
   * Request a new ride with atomic lock.
   * Endpoint: POST /api/v1/rides/request
   */
  async requestRide(payload: RequestRidePayload) {
    const res = await apiRequest(`${API_ROOT}/rides/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },

  /**
   * Check active ride status.
   * Endpoint: GET /api/v1/rides/active
   */
  async getActiveRide() {
    const res = await apiRequest(`${API_ROOT}/rides/active`, { method: 'GET' });
    return res?.data || res;
  },

  /**
   * Cancel an ongoing ride.
   * Endpoint: POST /api/v1/rides/<rideId>/cancel
   */
  async cancelRide(rideId: string, reason: string, comments = '') {
    const res = await apiRequest(`${API_ROOT}/rides/${rideId}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, comments }),
    });
    return res;
  },

  /**
   * Rate driver after ride completion.
   * Endpoint: POST /api/v1/rides/<rideId>/rate
   */
  async rateDriver(rideId: string, rating: number, feedback = '') {
    const res = await apiRequest(`${API_ROOT}/rides/${rideId}/rate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating, feedback }),
    });
    return res;
  },

  /**
   * Get popular outstation corridors across Andhra Pradesh.
   * Endpoint: GET /api/v1/rides/outstation/popular-routes
   */
  async getOutstationPopularRoutes() {
    const res = await apiRequest(`${API_ROOT}/rides/outstation/popular-routes`, { method: 'GET' });
    return res?.data || res;
  },

  /**
   * Outstation fare estimate (One way / Round trip).
   * Endpoint: POST /api/v1/rides/outstation/estimate
   */
  async getOutstationEstimate(payload: Record<string, unknown>) {
    const res = await apiRequest(`${API_ROOT}/rides/outstation/estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },
};

// ── 18. Spiritual Temples & Darshan Discovery API ───────────────────────

export interface TempleDistrict {
  id: string;
  name: string;
  count: number;
  tagline: string;
  image: string;
}

export interface TempleItem {
  id: string;
  name: string;
  district: string;
  city: string;
  primary_deity?: string;
  darshan_timings?: string;
  dress_code?: string;
  images?: string[];
  description?: string;
  festivals?: string[];
  rating?: number;
}

export const templesApi = {
  /**
   * List all verified Andhra Pradesh districts with temple counts.
   * Endpoint: GET /api/v1/temples/districts
   */
  async getDistricts(): Promise<TempleDistrict[]> {
    const res = await apiRequest(`${API_ROOT}/temples/districts`, { method: 'GET' });
    return (res?.data || res) as TempleDistrict[];
  },

  /**
   * List temples filtered by district or keyword.
   * Endpoint: GET /api/v1/temples/?district=...&search=...
   */
  async getTemples(params: { district?: string; search?: string } = {}): Promise<TempleItem[]> {
    const q = new URLSearchParams();
    if (params.district) q.set('district', params.district);
    if (params.search) q.set('search', params.search);
    const queryString = q.toString() ? `?${q.toString()}` : '';
    const res = await apiRequest(`${API_ROOT}/temples/${queryString}`, { method: 'GET' });
    return (res?.data || res) as TempleItem[];
  },

  /**
   * Get single temple details.
   * Endpoint: GET /api/v1/temples/<templeId>
   */
  async getTempleById(id: string): Promise<TempleItem> {
    const res = await apiRequest(`${API_ROOT}/temples/${id}`, { method: 'GET' });
    return (res?.data || res) as TempleItem;
  },
};

// ── 17. Tour Guides Discovery API ───────────────────────────────────────

export interface TourGuide {
  id: string;
  guideId: string;
  name: string;
  phone: string;
  email: string;
  gender: string;
  languages: string[];
  specialties: string[];
  experienceYears: number;
  averageRating: number;
  totalReviews: number;
  completedTours: number;
  hourlyRate: number;
  dailyRate: number;
  city: string;
  district: string;
  state: string;
  latitude: number;
  longitude: number;
  profileImage: string;
  coverImage?: string;
  bio: string;
  isAvailableNow: boolean;
  availableToday: boolean;
  isVerified: boolean;
  featuredGuide: boolean;
}

export const tourGuidesApi = {
  /**
   * List all verified Andhra tour guides.
   * Endpoint: GET /api/v1/tourguides/
   */
  async getAll(): Promise<TourGuide[]> {
    const res = await apiRequest(`${API_ROOT}/tourguides/`, { method: 'GET' });
    return (res?.data || res) as TourGuide[];
  },

  /**
   * Search guides by keyword.
   * Endpoint: GET /api/v1/tourguides/search?q=...
   */
  async search(query: string): Promise<TourGuide[]> {
    const res = await apiRequest(`${API_ROOT}/tourguides/search?q=${encodeURIComponent(query)}`, {
      method: 'GET',
    });
    return (res?.data || res) as TourGuide[];
  },

  /**
   * Find guides near GPS coordinates.
   * Endpoint: GET /api/v1/tourguides/nearby?lat=...&lng=...
   */
  async getNearby(lat: number, lng: number): Promise<TourGuide[]> {
    const res = await apiRequest(`${API_ROOT}/tourguides/nearby?lat=${lat}&lng=${lng}`, {
      method: 'GET',
    });
    return (res?.data || res) as TourGuide[];
  },

  /**
   * Get guides for a specific Andhra city.
   * Endpoint: GET /api/v1/tourguides/city/<city>
   */
  async getByCity(city: string): Promise<TourGuide[]> {
    const res = await apiRequest(`${API_ROOT}/tourguides/city/${encodeURIComponent(city)}`, {
      method: 'GET',
    });
    return (res?.data || res) as TourGuide[];
  },
};

// ── 20. News & Regional Andhra Feeds API ────────────────────────────────

export interface NewsArticle {
  id: string;
  title: string;
  description: string;
  source: string;
  sourceName?: string;
  category: string;
  language: string;
  imageUrl?: string;
  mediaUrl?: string;
  link: string;
  sourceUrl?: string;
  publishedAt: string;
  likeCount?: number;
  shareCount?: number;
}

export const newsApi = {
  /**
   * Get live curated Andhra news feed.
   * Endpoint: GET /api/v1/news/feed?category=...&language=...&limit=...
   */
  async getFeed(params: { category?: string; language?: string; limit?: number } = {}): Promise<NewsArticle[]> {
    const q = new URLSearchParams();
    if (params.category) q.set('category', params.category);
    if (params.language) q.set('language', params.language);
    if (params.limit) q.set('limit', String(params.limit));
    const res = await apiRequest(`${API_ROOT}/news/feed?${q.toString()}`, { method: 'GET' });
    return (res?.data || res) as NewsArticle[];
  },

  /**
   * Trigger non-blocking RSS sync.
   * Endpoint: POST /api/v1/news/sync
   */
  async syncNews() {
    return apiRequest(`${API_ROOT}/news/sync`, { method: 'POST' });
  },
};

// ── 13. Bus Booking Service (SRDV Aggregator) ───────────────────────────

export const busBookingApi = {
  /**
   * Search bus cities by prefix.
   * Endpoint: GET /api/v1/bus/cities?q=...
   */
  async searchCities(q: string) {
    const res = await apiRequest(`${API_ROOT}/bus/cities?q=${encodeURIComponent(q)}`, { method: 'GET' });
    return res?.data || res;
  },

  /**
   * Search available buses between origin and destination.
   * Endpoint: POST /api/v1/bus/search
   */
  async searchBuses(payload: { OriginId: string; DestinationId: string; DateOfJourney: string }) {
    const res = await apiRequest(`${API_ROOT}/bus/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },

  /**
   * Fetch bus seat layout.
   * Endpoint: POST /api/v1/bus/seat-layout
   */
  async getSeatLayout(payload: { TripId: string; ResultIndex: string }) {
    const res = await apiRequest(`${API_ROOT}/bus/seat-layout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },
};

// ── 14. Flight Booking Service (SRDV Aggregator) ────────────────────────

export const flightBookingApi = {
  /**
   * Search airports by query code or city.
   * Endpoint: GET /api/v1/flights/airports?q=...
   */
  async searchAirports(q: string) {
    const res = await apiRequest(`${API_ROOT}/flights/airports?q=${encodeURIComponent(q)}`, { method: 'GET' });
    return res?.data || res;
  },

  /**
   * Search flights.
   * Endpoint: POST /api/v1/flights/search
   */
  async searchFlights(payload: Record<string, unknown>) {
    const res = await apiRequest(`${API_ROOT}/flights/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },
};

// ── 15. Hotel Booking Service (SRDV Aggregator) ─────────────────────────

export const hotelBookingApi = {
  /**
   * Search hotel cities by query.
   * Endpoint: GET /api/v1/hotels/cities?q=...
   */
  async searchCities(q: string) {
    const res = await apiRequest(`${API_ROOT}/hotels/cities?q=${encodeURIComponent(q)}`, { method: 'GET' });
    return res?.data || res;
  },

  /**
   * Search hotels.
   * Endpoint: POST /api/v1/hotels/search
   */
  async searchHotels(payload: Record<string, unknown>) {
    const res = await apiRequest(`${API_ROOT}/hotels/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },

  /**
   * Fetch rooms for selected hotel.
   * Endpoint: POST /api/v1/hotels/rooms
   */
  async getHotelRooms(payload: { HotelCode: string; ResultIndex?: number; TraceId: string }) {
    const res = await apiRequest(`${API_ROOT}/hotels/rooms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },
};

// ── 16. Food Delivery & Authentic Andhra Dining ─────────────────────────

export const foodApi = {
  /**
   * List restaurants in given city.
   * Endpoint: GET /api/v1/food/restaurants?city=...
   */
  async getRestaurants(city = 'Vijayawada') {
    const res = await apiRequest(`${API_ROOT}/food/restaurants?city=${encodeURIComponent(city)}`, {
      method: 'GET',
    });
    return res?.data || res;
  },

  /**
   * Get restaurant menu items.
   * Endpoint: GET /api/v1/food/products?restaurant_id=...
   */
  async getProducts(restaurantId: string) {
    const res = await apiRequest(`${API_ROOT}/food/products?restaurant_id=${encodeURIComponent(restaurantId)}`, {
      method: 'GET',
    });
    return res?.data || res;
  },
};

// ── 21. Offers & Customer Support API ───────────────────────────────────

export const offersApi = {
  /**
   * Validate promo coupon code.
   * Endpoint: POST /api/v1/offers/validate
   */
  async validateCoupon(payload: { code: string; fare_amount: number; service?: string }) {
    const res = await apiRequest(`${API_ROOT}/offers/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },

  /**
   * Submit support ticket.
   * Endpoint: POST /api/v1/support/tickets
   */
  async submitSupportTicket(payload: { category: string; subject: string; description: string; ride_id?: string }) {
    const res = await apiRequest(`${API_ROOT}/support/tickets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res?.data || res;
  },
};

// ── 12. In-App Notifications API ────────────────────────────────────────

export const notificationsApi = {
  /**
   * List user in-app notifications.
   * Endpoint: GET /api/v1/notifications/
   */
  async getAll() {
    const res = await apiRequest(`${API_ROOT}/notifications/`, { method: 'GET' });
    return res?.data || res;
  },

  /**
   * Get unread notifications count badge.
   * Endpoint: GET /api/v1/notifications/unread-count
   */
  async getUnreadCount(): Promise<number> {
    const res = await apiRequest(`${API_ROOT}/notifications/unread-count`, { method: 'GET' });
    const count = res?.data?.count ?? res?.count ?? 0;
    return typeof count === 'number' ? count : Number(count) || 0;
  },

  /**
   * Register FCM push device token.
   * Endpoint: POST /api/v1/notifications/devices/register
   */
  async registerDevice(deviceToken: string, deviceType = 'WEB', appVersion = '2.0.0') {
    return apiRequest(`${API_ROOT}/notifications/devices/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device_token: deviceToken,
        device_type: deviceType,
        app_version: appVersion,
      }),
    });
  },
};
